
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { LogOut, Settings, BarChart, BookOpen, Menu, Mail, Plus, Target, Users, Calendar, Sparkles } from "lucide-react";
import Logo from "@/components/Logo";
import { supabase } from "@/integrations/supabase/client";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import HeaderWithPWA from "./HeaderWithPWA";
import { UserAvatar } from "@/components/profile/UserAvatar";
import AnimatedHeaderBg from "./AnimatedHeaderBg";
import { useAppShell } from "./AppShellContext";

const Header = () => {
  const { inShell } = useAppShell();
  const { user, signOut } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isInstructor, setIsInstructor] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) {
        setIsAdmin(false);
        setIsInstructor(false);
        setIsLoading(false);
        return;
      }

      try {
        const { data: isAdminData, error: adminError } = await supabase.rpc('has_role', {
          _user_id: user.id,
          _role: 'admin'
        });
        
        const { data: isInstructorData, error: instructorError } = await supabase.rpc('has_role', {
          _user_id: user.id,
          _role: 'instructor'
        });
        
        if (adminError) {
          console.error('Error checking admin role:', adminError);
          setIsAdmin(false);
        } else {
          setIsAdmin(isAdminData);
        }

        if (instructorError) {
          console.error('Error checking instructor role:', instructorError);
          setIsInstructor(false);
        } else {
          setIsInstructor(isInstructorData);
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [user]);

  useEffect(() => {
    setSheetOpen(false);
  }, [location.pathname]);

  // When mounted inside the new AppShell, the sidebar+topbar already
  // provide navigation — render nothing to avoid duplicated chrome.
  if (inShell) return null;

  const handleSignOut = async (e: React.MouseEvent) => {
    e.preventDefault();
    await signOut();
  };

  const handleMenuItemClick = (path: string) => {
    setSheetOpen(false);
    navigate(path);
  };

  const MobileMenu = () => (
    <div className="flex flex-col space-y-4 py-4">
      <Link to="/" className="text-lg font-medium text-gray-900 hover:text-primary px-4" onClick={() => setSheetOpen(false)}>
        Home
      </Link>
      <div
        className="text-lg font-medium text-primary hover:text-primary/80 flex items-center px-4 cursor-pointer"
        onClick={() => handleMenuItemClick('/chat')}
      >
        <Sparkles className="h-5 w-5 mr-2" />
        AI Chat
      </div>
      {!user && (
        <div 
          className="text-lg font-medium text-blue-600 hover:text-blue-700 flex items-center px-4 cursor-pointer"
          onClick={() => handleMenuItemClick('/parent/auth')}
        >
          <Users className="h-5 w-5 mr-2" />
          Parent Portal
        </div>
      )}
      {user && (
        <>
          <div 
            className="text-lg font-medium text-gray-900 hover:text-primary px-4 cursor-pointer"
            onClick={() => handleMenuItemClick('/dashboard')}
          >
            Dashboard
          </div>
          <div 
            className="text-lg font-medium text-gray-900 hover:text-primary flex items-center px-4 cursor-pointer"
            onClick={() => handleMenuItemClick('/analysis')}
          >
            <BarChart className="h-5 w-5 mr-2" />
            Analysis
          </div>
          {isAdmin && (
            <>
              <div className="font-medium text-gray-700 text-lg px-4 py-2">Admin</div>
              <div 
                className="text-base font-medium text-gray-900 hover:text-primary px-8 cursor-pointer"
                onClick={() => handleMenuItemClick('/admin/questions')}
              >
                Manage Questions
              </div>
              <div 
                className="text-base font-medium text-gray-900 hover:text-primary px-8 cursor-pointer"
                onClick={() => handleMenuItemClick('/admin/generator')}
              >
                Question Generator
              </div>
              <div 
                className="text-base font-medium text-gray-900 hover:text-primary flex items-center px-8 cursor-pointer"
                onClick={() => handleMenuItemClick('/admin/subjects')}
              >
                <BookOpen className="h-4 w-4 mr-2" />
                Manage Subjects
              </div>
              <div 
                className="text-base font-medium text-gray-900 hover:text-primary px-8 cursor-pointer"
                onClick={() => handleMenuItemClick('/admin/reports')}
              >
                Reported Questions
              </div>
              <div 
                className="text-base font-medium text-gray-900 hover:text-primary px-8 cursor-pointer"
                onClick={() => handleMenuItemClick('/admin/logs')}
              >
                AI Logs
              </div>
              <div 
                className="text-base font-medium text-gray-900 hover:text-primary flex items-center px-8 cursor-pointer"
                onClick={() => handleMenuItemClick('/admin/email-templates')}
              >
                <Mail className="h-4 w-4 mr-2" />
                Email Templates
              </div>
              <div 
                className="text-base font-medium text-gray-900 hover:text-primary px-8 cursor-pointer"
                onClick={() => handleMenuItemClick('/admin/email-campaigns')}
              >
                Email Campaigns
              </div>
            </>
          )}
          {isInstructor && (
            <>
              <div className="font-medium text-gray-700 text-lg px-4 py-2">Instructor</div>
              <div 
                className="text-base font-medium text-gray-900 hover:text-primary px-8 cursor-pointer"
                onClick={() => handleMenuItemClick('/instructor/dashboard')}
              >
                Dashboard
              </div>
              <div 
                className="text-base font-medium text-gray-900 hover:text-primary px-8 cursor-pointer"
                onClick={() => handleMenuItemClick('/instructor/tests')}
              >
                Tests
              </div>
              <div 
                className="text-base font-medium text-gray-900 hover:text-primary px-8 cursor-pointer"
                onClick={() => handleMenuItemClick('/instructor/students')}
              >
                Students
              </div>
              <div 
                className="text-base font-medium text-gray-900 hover:text-primary px-8 cursor-pointer"
                onClick={() => handleMenuItemClick('/instructor/tutoring')}
              >
                Schedule
              </div>
            </>
          )}
          <div
            className="text-lg font-medium text-gray-900 hover:text-primary flex items-center px-4 cursor-pointer"
            onClick={() => handleMenuItemClick('/settings')}
          >
            <Settings className="h-5 w-5 mr-2" />
            Settings
          </div>
          <button 
            onClick={(e) => {
              handleSignOut(e);
              setSheetOpen(false);
            }} 
            className="text-lg font-medium text-gray-900 hover:text-primary flex items-center px-4"
          >
            <LogOut className="h-5 w-5 mr-2" />
            Sign Out
          </button>
        </>
      )}
      {!user && (
        <div 
          className="text-lg font-medium text-gray-900 hover:text-primary px-4 cursor-pointer"
          onClick={() => handleMenuItemClick('/auth')}
        >
          Sign In
        </div>
      )}
    </div>
  );

  const DesktopMenu = () => (
    <div className="flex items-center space-x-1 md:space-x-4">
      <Link to="/chat" className="text-sm md:text-base font-semibold text-primary hover:text-primary/80 flex items-center">
        <Sparkles className="h-4 w-4 mr-1" />
        AI Chat
      </Link>
      {user ? (
        <>
          <Link to="/dashboard" className="text-sm md:text-base font-medium text-gray-700 hover:text-gray-900 flex items-center">
            Dashboard
          </Link>
          
          
          <Link to="/analysis" className="text-sm md:text-base font-medium text-gray-700 hover:text-gray-900 flex items-center">
            <BarChart className="h-4 w-4 mr-1" />
            Analysis
          </Link>
          
          {isAdmin && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="link" className="text-sm md:text-base font-medium text-gray-700 hover:text-gray-900">
                  Admin
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/admin/questions">
                    Manage Questions
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/generator">
                    Question Generator
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/subjects">
                    <BookOpen className="mr-2 h-4 w-4" />
                    Manage Subjects
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/reports">
                    Reported Questions
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/logs">
                    AI Logs
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/email-templates">
                    <Mail className="mr-2 h-4 w-4" />
                    Email Templates
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/admin/email-campaigns">
                    Email Campaigns
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          {isInstructor && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="link" className="text-sm md:text-base font-medium text-gray-700 hover:text-gray-900">
                  Instructor
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
               <DropdownMenuItem asChild>
                  <Link to="/instructor/dashboard">
                    <BookOpen className="mr-2 h-4 w-4" />
                    Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/instructor/tests">
                    <Target className="mr-2 h-4 w-4" />
                    Tests
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/instructor/students">
                    <Users className="mr-2 h-4 w-4" />
                    Students
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/instructor/tutoring">
                    <Calendar className="mr-2 h-4 w-4" />
                    Schedule
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          
          <span className="hidden md:inline-block px-2 py-1 bg-primary/10 text-primary rounded-md text-xs">
            {user.email}
          </span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0 rounded-full overflow-hidden">
                <span className="sr-only">Open user menu</span>
                <div className="w-full h-full flex items-center justify-center">
                  <UserAvatar userId={user.id} size="sm" editable={false} fullSize={true} />
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem asChild>
                <Link to="/settings" className="flex items-center">
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleSignOut} className="flex items-center">
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </>
      ) : (
        <>
          <Link to="/parent/auth" className="text-sm md:text-base font-medium text-blue-600 hover:text-blue-700 flex items-center">
            <Users className="h-4 w-4 mr-1" />
            Parent Portal
          </Link>
          <Link to="/auth" className="text-sm md:text-base font-medium text-gray-700 hover:text-gray-900">
            Sign In
          </Link>
        </>
      )}
      <div className="ml-2">
        <HeaderWithPWA />
      </div>
    </div>
  );

  return (
    <header className="bg-white shadow-sm border-b sticky top-0 z-10 relative overflow-hidden">
      {/* Add our animated background component */}
      <AnimatedHeaderBg />
      
      <div className="container mx-auto px-4 md:px-6 flex justify-between items-center h-16 relative z-10">
        <Link to="/" className="flex items-center space-x-2">
          <Logo horizontal />
        </Link>

        {isMobile ? (
          <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[350px]">
              <MobileMenu />
            </SheetContent>
          </Sheet>
        ) : (
          <DesktopMenu />
        )}
      </div>
    </header>
  );
};

export default Header;
